// src/services/firestorePaths.js
//
// Phase 5/6 — Centralized Firestore path helpers.
//
// Single source of truth for every Firestore path the app uses. Instead of
// hardcoding collection names inline (e.g. collection(db, "consents")), call a
// helper:
//
//   import { getConsentsPath } from "../services/firestorePaths";
//   collection(db, getConsentsPath());           // collection ref
//   doc(db, getConsentsPath(), consentId);        // document ref
//
// Each helper returns a PATH STRING. The modular Firestore SDK accepts a
// slash-joined path for both collection() and doc().
//
// FEATURE FLAG — REACT_APP_USE_TENANT_PATHS:
//   - unset / "false"  -> helpers return the LEGACY root paths (current
//     behavior; the app runs exactly as before, no rules/data change needed).
//   - "true"           -> helpers return the new multi-tenant paths
//     (tenants/{tenantId}/sites/{siteId}/...). Requires the matching Firestore
//     security rules to be deployed first, or every read/write is denied.
//
// The active tenantId/siteId come from services/activeTenant.js.
//
// AUTH COLLECTIONS (user_roles, otp_codes, otp_sessions) ALWAYS resolve to the
// ROOT, regardless of the flag. The Cloud Functions (sendOtp/verifyOtp) write
// them at the root via the Admin SDK and login reads roles before a site is
// known — repointing them would break 2FA. Do not change this without also
// updating the functions and rules.

import { getActiveTenantId, getActiveSiteId } from "./activeTenant";

const USE_TENANT_PATHS = process.env.REACT_APP_USE_TENANT_PATHS === "true";

// Collections that must stay at the project root (auth / 2FA).
const ROOT_ONLY = new Set(["user_roles", "otp_codes", "otp_sessions"]);

const requireId = (label, value) => {
  if (value === undefined || value === null || value === "") {
    throw new Error(`firestorePaths: "${label}" is required but was empty.`);
  }
  return value;
};

// ---------------------------------------------------------------------------
// Core resolver — turns a logical collection name into its actual path.
// ---------------------------------------------------------------------------
export const getCollectionPath = (name) => {
  requireId("name", name);
  if (ROOT_ONLY.has(name) || !USE_TENANT_PATHS) return name; // legacy / root
  const tenantId = requireId("active tenantId", getActiveTenantId());
  const siteId = requireId("active siteId", getActiveSiteId());
  return `tenants/${tenantId}/sites/${siteId}/${name}`;
};

// For collectionGroup() queries (e.g. "requests"), which match a collection id
// at ANY depth: returns true only for doc paths that belong to the CURRENTLY
// ACTIVE scope. With the flag off, that's the legacy root tree (no "tenants/"
// prefix); with it on, the active tenant+site subtree. Use it to filter
// collectionGroup results so root and tenant copies aren't double-counted.
export const isActiveScopePath = (docPath) => {
  if (!docPath) return false;
  if (!USE_TENANT_PATHS) return !docPath.startsWith("tenants/");
  return docPath.startsWith(
    `tenants/${getActiveTenantId()}/sites/${getActiveSiteId()}/`
  );
};

// Tenant/site building blocks for the ACTIVE context.
export const getTenantDocPath = () =>
  `tenants/${requireId("active tenantId", getActiveTenantId())}`;
export const getSitesCollectionPath = () => `${getTenantDocPath()}/sites`;
export const getSiteDocPath = () =>
  `${getSitesCollectionPath()}/${requireId("active siteId", getActiveSiteId())}`;

// Explicit-arg variants for tenant/site MANAGEMENT (owner/admin consoles need
// to read/write tenants & sites other than the active one). These never depend
// on the feature flag — the multi-tenant tree always lives under "tenants/".
export const getTenantsCollectionPath = () => "tenants";
export const getTenantDocPathFor = (tenantId) =>
  `tenants/${requireId("tenantId", tenantId)}`;
export const getSitesCollectionPathFor = (tenantId) =>
  `${getTenantDocPathFor(tenantId)}/sites`;
export const getSiteDocPathFor = (tenantId, siteId) =>
  `${getSitesCollectionPathFor(tenantId)}/${requireId("siteId", siteId)}`;

// ---------------------------------------------------------------------------
// Top-level collection helpers (no args — resolved from the active store)
// ---------------------------------------------------------------------------
export const getConsentsPath = () => getCollectionPath("consents");

// ---------------------------------------------------------------------------
// Referral collections — physically isolated per site.
//
// Each site's referrals live under ITS OWN path (tenants/{t}/sites/{siteId}/...),
// so a site's data is never mixed with another's. STAFF operations resolve to
// the ACTIVE site (getCollectionPath). The UNAUTHENTICATED patient flow has no
// active session, so the referral LINK carries the siteId and the questionnaire
// page uses the *ForSite variants with the site from the URL. The DEFAULT site
// is the fallback for legacy links minted before the site was in the URL — those
// are all Dubois, which is exactly the default.
// ---------------------------------------------------------------------------
const REFERRAL_DEFAULT_TENANT = process.env.REACT_APP_DEFAULT_TENANT || "lung-innovations";
export const REFERRAL_DEFAULT_SITE = process.env.REACT_APP_DEFAULT_SITE || "lin-dubois1";

// Shared by every UNAUTHENTICATED patient-facing page (referral questionnaire,
// e-sign consent). Those pages have no session and therefore no active site, so
// the site comes from the URL and is passed in explicitly here.
const publicSiteCollection = (siteId, name) =>
  USE_TENANT_PATHS
    ? `tenants/${REFERRAL_DEFAULT_TENANT}/sites/${requireId("siteId", siteId)}/${name}`
    : name;

// Staff paths — the ACTIVE site.
export const getReferralSubmissionsPath = () => getCollectionPath("referral_submissions");
export const getReferralRecipientsPath = () => getCollectionPath("referral_recipients");
export const getReferralPendingPath = () => getCollectionPath("referral_pending");

// Explicit-site paths — for the patient questionnaire page, which resolves the
// site from the referral link rather than an active session.
export const getReferralRecipientsPathForSite = (siteId) =>
  publicSiteCollection(siteId, "referral_recipients");
export const getReferralSubmissionsPathForSite = (siteId) =>
  publicSiteCollection(siteId, "referral_submissions");

// Private, staff-only store for a recipient's MRN (PHI). Kept in a subcollection
// of the recipient rather than on the public recipient doc, so the patient's
// browser (which reads the public recipient doc by token) never receives it.
export const getReferralRecipientMrnDocPath = (recipientId) =>
  `${getReferralRecipientsPath()}/${requireId("recipientId", recipientId)}/private/mrn`;
// Per-submission staff notes + audit trail (the "Raw Database" call-duty page).
// Built off the active-site submission base so staff paths line up.
export const getReferralSubmissionNotesPath = (submissionId) =>
  `${getReferralSubmissionsPath()}/${requireId("submissionId", submissionId)}/notes`;
export const getReferralSubmissionAuditPath = (submissionId) =>
  `${getReferralSubmissionsPath()}/${requireId("submissionId", submissionId)}/audit`;
export const getDuplicatesPath = () => getCollectionPath("duplicates");
// Coordinator enrollments waiting for admin approval onto the master list.
export const getEnrollmentRequestsPath = () => getCollectionPath("enrollment_requests");
export const getStagedPatientsPath = () => getCollectionPath("staged_patients");

// ---------------------------------------------------------------------------
// E-sign consent — explicit-site paths (D-16).
//
// Same problem, same shape, as the referral questionnaire above. The consent
// link is opened on a PATIENT's phone, which has never had an active site set,
// so a link minted at any site other than the default resolved against the
// default and the record was never found. The link now carries the siteId and
// the signing page resolves all three of its hops — find the patient, write the
// consent, stamp the record — against that site. Legacy /esign/:token links
// (no siteId) fall back to the default site, which is where every link minted
// before this change was issued.
// ---------------------------------------------------------------------------
export const getStagedPatientsPathForSite = (siteId) =>
  publicSiteCollection(siteId, "staged_patients");
export const getConsentsPathForSite = (siteId) =>
  publicSiteCollection(siteId, "consents");
export const getSourcexPatientsPath = () => getCollectionPath("sourcex_patients");
export const getSourcexTemplatesPath = () => getCollectionPath("sourcex_templates");
// Version-history snapshots for one template — a SUBCOLLECTION, not a field on
// the template doc. A template's own content plus every past snapshot sharing
// one document's 1 MiB budget is what broke approval once a template had a
// few versions behind it; each version getting its own document removes that
// shared ceiling entirely.
export const getTemplateVersionsPath = (templateId) =>
  `${getSourcexTemplatesPath()}/${requireId("templateId", templateId)}/versions`;
export const getStudyContactsPath = () => getCollectionPath("study_contacts");
export const getStudiesPath = () => getCollectionPath("studies");
export const getStudiesCopyPath = () => getCollectionPath("studies_copy");
export const getEmployeeCredentialsPath = () => getCollectionPath("employeeCredentials");
// UI revamp: full names on studies (Lead CRC) linked to coordinators (crcNames.js).
export const getCrcNamesPath = () => getCollectionPath("crc_names");

export const getCoordinatorsPath = () => getCollectionPath("coordinators");
export const getCoordinatorEnrollmentsPath = () =>
  getCollectionPath("coordinatorEnrollments");
export const getCoordinatorEnrollmentArchivesPath = () =>
  getCollectionPath("coordinatorEnrollmentArchives");
export const getCoordinatorWeeklyPath = () => getCollectionPath("coordinatorWeekly");

// Period-scoped enrollment counts.
//
// The counts used to be one running number per coordinator at the ROOT of these
// collections, with no period attached — so "August" and "September" could not
// both exist, and the month was only named when somebody clicked a reset button.
// Keyed by period, both exist at once, a new period starts empty on its own, and
// a manager back from leave can enter last month without disturbing this one.
//
// The legacy root documents are deliberately left in place by the migration:
// they are small, and they are the rollback.
export const getEnrollmentPeriodPath = (periodId) =>
  `${getCoordinatorEnrollmentsPath()}/${requireId("periodId", periodId)}/coordinators`;
export const getWeeklyPeriodPath = (periodId) =>
  `${getCoordinatorWeeklyPath()}/${requireId("periodId", periodId)}/coordinators`;
export const getMonthlyEnrollmentGoalsPath = () =>
  getCollectionPath("monthlyEnrollmentGoals");

// Screening counts and goal — the Coordinator page's second goal circle. Same
// period-keyed shape as the enrollment collections above, entered by hand.
export const getCoordinatorScreeningsPath = () => getCollectionPath("coordinatorScreenings");
export const getCoordinatorScreeningsWeeklyPath = () =>
  getCollectionPath("coordinatorScreeningsWeekly");
export const getScreeningPeriodPath = (periodId) =>
  `${getCoordinatorScreeningsPath()}/${requireId("periodId", periodId)}/coordinators`;
export const getScreeningWeeklyPeriodPath = (periodId) =>
  `${getCoordinatorScreeningsWeeklyPath()}/${requireId("periodId", periodId)}/coordinators`;
export const getMonthlyScreeningGoalsPath = () => getCollectionPath("monthlyScreeningGoals");
// Admin-set rules turning weekly-report numbers into goal points (Phase 2):
// one doc per goal per start month, `{metric}__{YYYY-MM}`.
export const getGoalRuleSetsPath = () => getCollectionPath("goalRuleSets");
// Admin decisions on rule points: an append-only ledger, and running totals per
// goal + month + coordinator (`{metric}__{YYYY-MM}__{name}`).
export const getGoalPointDecisionsPath = () => getCollectionPath("goalPointDecisions");
export const getGoalPointTotalsPath = () => getCollectionPath("goalPointTotals");
export const getWeeklyReportsPath = () => getCollectionPath("weekly_reports");
// Patient-level weekly reports. The patient list sits apart from the counts
// (same doc id as its weekly_reports doc) because weekly_reports is readable by
// every site role; this one only by site admins and the report's coordinator.
export const getWeeklyReportPatientsPath = () => getCollectionPath("weekly_report_patients");
// One in-progress report per user, keyed by uid; only its owner can read it.
export const getWeeklyReportDraftsPath = () => getCollectionPath("weekly_report_drafts");
export const getSponsorQueriesPath = () => getCollectionPath("sponsorQueries");
export const getMonitorFindingsPath = () => getCollectionPath("monitorFindings");
export const getMonitorAssignmentsPath = () => getCollectionPath("monitor_assignments");
export const getMonitorRequestsPath = () => getCollectionPath("monitor_requests");
export const getEbinderPath = () => getCollectionPath("ebinder");
export const getPatientAuditsPath = () => getCollectionPath("patient_audits");
export const getPatientNotesPath = () => getCollectionPath("patient_notes");
export const getPatientPresencePath = () => getCollectionPath("patient_presence");

// ---------------------------------------------------------------------------
// Subcollection helpers
// ---------------------------------------------------------------------------
export const getStudyPatientsPath = (studyId) =>
  `${getStudiesCopyPath()}/${requireId("studyId", studyId)}/patients`;
export const getStudyPotentialPatientsPath = (studyId) =>
  `${getStudiesCopyPath()}/${requireId("studyId", studyId)}/potentialPatients`;

export const getPatientAuditEventsPath = (patientId) =>
  `${getPatientAuditsPath()}/${requireId("patientId", patientId)}/events`;

// Append-only audit trail for a source-document TEMPLATE — who changed which
// question, when, and from what to what. Same shape and the same guarantees as
// the patient trail above (see templateAuditService.js): its own collection so
// an audit write can never grow the template document, one document per event
// so nothing is ever rewritten, and never anything but template structure —
// a template is a blank form shared by every patient and holds no PHI to leak.
export const getSourcexTemplateAuditsPath = () =>
  getCollectionPath("sourcex_template_audits");
export const getSourcexTemplateAuditEventsPath = (templateId) =>
  `${getSourcexTemplateAuditsPath()}/${requireId("templateId", templateId)}/events`;
export const getPatientNotesEntriesPath = (patientId) =>
  `${getPatientNotesPath()}/${requireId("patientId", patientId)}/notes`;
// Replies/annotations threaded under a single saved note. Additive & append-only —
// original notes are never mutated; replies live in their own subcollection.
export const getPatientNoteRepliesPath = (patientId, noteId) =>
  `${getPatientNotesEntriesPath(patientId)}/${requireId(
    "noteId",
    noteId
  )}/replies`;
export const getPatientPresenceViewersPath = (patientId) =>
  `${getPatientPresencePath()}/${requireId("patientId", patientId)}/viewers`;

export const getMonitorRequestsForUserPath = (uid) =>
  `${getMonitorRequestsPath()}/${requireId("uid", uid)}/requests`;

export const getEbinderSectionPath = (studyId, sectionId) =>
  `${getEbinderPath()}/${requireId("studyId", studyId)}/${requireId(
    "sectionId",
    sectionId
  )}`;

// ---------------------------------------------------------------------------
// Protocol → SOA extraction pipeline (Phase 9) — everything hangs off the
// SITE-SCOPED studies tree, so protocol PDFs, page images, OCR, extractions and
// generated source docs are tenant/site isolated exactly like the rest of the
// app (and gated by the matching firestore.rules subcollection rules).
// ---------------------------------------------------------------------------
export const getStudyProtocolsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/protocols`;
export const getStudyProtocolDocPath = (studyId, protocolId) =>
  `${getStudyProtocolsPath(studyId)}/${requireId("protocolId", protocolId)}`;
export const getProtocolPagesPath = (studyId, protocolId) =>
  `${getStudyProtocolDocPath(studyId, protocolId)}/pages`;
export const getProtocolOcrPagesPath = (studyId, protocolId) =>
  `${getStudyProtocolDocPath(studyId, protocolId)}/ocrPages`;
export const getProtocolExtractionsPath = (studyId, protocolId) =>
  `${getStudyProtocolDocPath(studyId, protocolId)}/extractions`;
// The working, reviewable merged SOA extraction lives at extractions/soa.
export const getProtocolExtractionDocPath = (studyId, protocolId) =>
  `${getProtocolExtractionsPath(studyId, protocolId)}/soa`;
// The approved snapshot (frozen at approval time) lives at approvedSoa/soa.
export const getProtocolApprovedSoaPath = (studyId, protocolId) =>
  `${getStudyProtocolDocPath(studyId, protocolId)}/approvedSoa`;
export const getProtocolApprovedSoaDocPath = (studyId, protocolId) =>
  `${getProtocolApprovedSoaPath(studyId, protocolId)}/soa`;
// Generated per-visit source docs (one doc per visit), at the study level so AI
// retrieval can query them without knowing which protocol produced them.
export const getStudySourceDocVisitsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/sourceDocVisits`;
// Protocol Compare: old vs new protocol comparisons (admins only; server-written).
export const getProtocolComparisonsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/protocolComparisons`;
// Protocol versions (Version 1, 2, 3; server-written) and staff requests for a new version.
export const getProtocolVersionsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/protocolVersions`;
export const getProtocolVersionRequestsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/protocolVersionRequests`;
// The study's protocol history (append-only).
export const getProtocolEventsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/protocolEvents`;

// ---------------------------------------------------------------------------
// Blank eCRF → forms/fields/branching extraction (Phase 1b). Mirrors the
// protocol tree above and hangs off the same SITE-SCOPED studies path, so eCRF
// PDFs and their extractions are tenant/site isolated exactly like protocols.
//
//   studies/{studyId}/ecrfs/{ecrfId}                  <- uploaded eCRF doc
//   studies/{studyId}/ecrfs/{ecrfId}/chunks/{n}       <- per-chunk raw results
//   studies/{studyId}/ecrfs/{ecrfId}/extractions/ecrf <- merged + resolved
// ---------------------------------------------------------------------------
export const getStudyEcrfsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/ecrfs`;
export const getStudyEcrfDocPath = (studyId, ecrfId) =>
  `${getStudyEcrfsPath(studyId)}/${requireId("ecrfId", ecrfId)}`;
// Per-chunk results are kept so a failed run is diagnosable without re-reading
// the PDF, and so a single bad chunk can be re-run in isolation later.
export const getEcrfChunksPath = (studyId, ecrfId) =>
  `${getStudyEcrfDocPath(studyId, ecrfId)}/chunks`;
export const getEcrfExtractionsPath = (studyId, ecrfId) =>
  `${getStudyEcrfDocPath(studyId, ecrfId)}/extractions`;
// The merged, branch-resolved extraction — the study's structured eCRF source
// of truth. Everything downstream reads this, never the PDF.
export const getEcrfExtractionDocPath = (studyId, ecrfId) =>
  `${getEcrfExtractionsPath(studyId, ecrfId)}/ecrf`;

// ---------------------------------------------------------------------------
// SoA x eCRF join (Phase 2) — the cached mapping table.
//
//   studies/{studyId}/joins/current   <- the study's join, WITH human decisions
//
// ONE doc per study rather than one per (protocol, eCRF) pair. The whole point
// of the join is that a coordinator's confirmations survive re-computation
// (roadmap §5 rule 2), and mappings are keyed by (soaAssessmentKey, formId) —
// both derived from NAMES, not document ids. Keying the doc by document id
// instead would orphan every decision the moment a protocol is re-uploaded,
// which is exactly the failure mergeJoinDecisions exists to prevent. The
// protocol/eCRF it was computed from are recorded as fields.
// ---------------------------------------------------------------------------
export const getStudyJoinsPath = (studyId) =>
  `${getStudiesPath()}/${requireId("studyId", studyId)}/joins`;
export const getStudyJoinDocPath = (studyId) =>
  `${getStudyJoinsPath(studyId)}/current`;

// ---------------------------------------------------------------------------
// Per-site integrations (calendar, ...).
//
// One doc per integration, so a site's connection details are data rather than
// env vars or hardcoded constants — a new customer is a config doc, not a code
// change. The SECRET half of a connection (endpoint URL, shared secret, OAuth
// tokens) lives in a `private` subcollection that firestore.rules denies to all
// clients; only the Admin SDK reads it. Mirrors the referral-recipient MRN split.
//
// UNLIKE most helpers here, these ALWAYS resolve under tenants/{t}/sites/{s}/
// regardless of REACT_APP_USE_TENANT_PATHS. The `researchCalendar` Cloud
// Function reads them through its own siteBase() helper, which is always the
// tenant tree — so if these fell back to a root path with the flag off, the
// browser and the server would be looking at two different documents and the
// UI would silently edit config the calendar never uses. Same reasoning as the
// referral helpers above being pinned rather than flag-dependent.
//
// The *ForSite variants exist for the admin console, which manages sites other
// than the active one; using the active-site helpers there would write a site's
// calendar config onto whichever site the admin happened to have selected.
// ---------------------------------------------------------------------------
export const getIntegrationsPathFor = (tenantId, siteId) =>
  `${getSiteDocPathFor(tenantId, siteId)}/integrations`;
export const getCalendarIntegrationDocPathFor = (tenantId, siteId) =>
  `${getIntegrationsPathFor(tenantId, siteId)}/calendar`;

export const getIntegrationsPath = () =>
  getIntegrationsPathFor(getActiveTenantId(), getActiveSiteId());
export const getCalendarIntegrationDocPath = () =>
  getCalendarIntegrationDocPathFor(getActiveTenantId(), getActiveSiteId());

// Home page private cards (My notes, My quick links; UI revamp). Pinned to the
// tenant tree for the same reason as the integrations above: the
// saveHomePrivate function writes through siteBase(), which is always
// tenants/{t}/sites/{s}, so the browser must read that same document.
export const getHomePrivateDocPathFor = (tenantId, siteId, uid) =>
  `${getSiteDocPathFor(tenantId, siteId)}/home_private/${requireId("uid", uid)}`;
export const getHomePrivateDocPath = (uid) =>
  getHomePrivateDocPathFor(getActiveTenantId(), getActiveSiteId(), uid);

// ---------------------------------------------------------------------------
// Auth / global — ALWAYS root.
// ---------------------------------------------------------------------------
export const getUserRolesPath = () => "user_roles";
// Home page announcements (UI revamp): root, because a post can go to everyone
// across companies; each post names its audience (firestore.rules).
export const getAnnouncementsPath = () => "announcements";
export const getAnnouncementSeenPath = () => "announcement_seen";
export const getOtpCodesPath = () => "otp_codes";
export const getOtpSessionsPath = () => "otp_sessions";
